/**
 * Parties Section for Incident Editor Dialog (PROJ-007)
 * Manages affected parties (persons and companies) linked to incidents
 * Integrated into the "People" tab
 */

import { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription } from '@/components/ui/alert';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from '@/components/ui/select';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Separator } from '@/components/ui/separator';
import { Users, Trash2, Plus, AlertCircle, User, Building } from 'lucide-react';
import {
  listIncidentParties,
  addIncidentParty,
  removeIncidentParty
} from '@/lib/incidentPartyApi';
import type { IncidentParty } from '@/types/incidentParty';
import { PARTY_ROLES, PARTY_ROLE_LABELS } from '@/types/incidentParty';
import { toast } from '@/hooks/use-toast';

interface PartiesSectionProps {
  incidentId: string | null;
  isViewMode?: boolean;
}

export function PartiesSection({ incidentId, isViewMode = false }: PartiesSectionProps) {
  const [parties, setParties] = useState<IncidentParty[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Form state
  const [selectedType, setSelectedType] = useState<'person' | 'company'>('person');
  const [personId, setPersonId] = useState('');
  const [companyId, setCompanyId] = useState('');
  const [role, setRole] = useState('');

  useEffect(() => {
    if (incidentId) {
      loadParties();
    }
  }, [incidentId]);

  const loadParties = async () => {
    if (!incidentId) return;

    setIsLoading(true);
    try {
      const data = await listIncidentParties(incidentId, { includeDetails: true });
      setParties(data);
    } catch (error: any) {
      console.error('Error loading parties:', error);
      toast({
        title: 'Error',
        description: 'Error al cargar partes afectadas',
        variant: 'destructive'
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleAddParty = async () => {
    if (!role) {
      toast({
        title: 'Validación',
        description: 'Debes seleccionar un rol',
        variant: 'destructive'
      });
      return;
    }

    if (selectedType === 'person' && !personId) {
      toast({
        title: 'Validación',
        description: 'Debes ingresar un ID de persona',
        variant: 'destructive'
      });
      return;
    }

    if (selectedType === 'company' && !companyId) {
      toast({
        title: 'Validación',
        description: 'Debes ingresar un ID de empresa',
        variant: 'destructive'
      });
      return;
    }

    setIsSubmitting(true);
    try {
      await addIncidentParty(incidentId!, {
        personId: selectedType === 'person' ? personId : undefined,
        companyId: selectedType === 'company' ? companyId : undefined,
        role
      });

      toast({
        title: 'Éxito',
        description: 'Parte afectada agregada exitosamente'
      });

      // Reset form
      setPersonId('');
      setCompanyId('');
      setRole('');

      // Reload parties
      await loadParties();
    } catch (error: any) {
      toast({
        title: 'Error',
        description: error.message || 'Error al agregar parte afectada',
        variant: 'destructive'
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleRemoveParty = async (partyId: string) => {
    if (!confirm('¿Estás seguro de remover esta parte afectada?')) {
      return;
    }

    try {
      await removeIncidentParty(incidentId!, partyId);
      toast({
        title: 'Éxito',
        description: 'Parte afectada removida exitosamente'
      });
      await loadParties();
    } catch (error: any) {
      toast({
        title: 'Error',
        description: error.message || 'Error al remover parte afectada',
        variant: 'destructive'
      });
    }
  };

  if (!incidentId) {
    return (
      <Alert>
        <AlertCircle className="h-4 w-4" />
        <AlertDescription>
          Guarda el incidente primero para gestionar partes afectadas.
        </AlertDescription>
      </Alert>
    );
  }

  return (
    <div className="space-y-6">
      <Alert>
        <Users className="h-4 w-4" />
        <AlertDescription>
          Personas y empresas involucradas en el incidente (afectados, testigos, investigadores, etc.)
        </AlertDescription>
      </Alert>

      {/* Add Party Form */}
      {!isViewMode && (
        <Card>
          <CardHeader>
            <CardTitle>Agregar Parte Afectada</CardTitle>
            <CardDescription>
              Asocia una persona o empresa al incidente con un rol específico
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Tipo</Label>
                <Select
                  value={selectedType}
                  onValueChange={(value: 'person' | 'company') => setSelectedType(value)}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="person">Persona</SelectItem>
                    <SelectItem value="company">Empresa</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label>Rol</Label>
                <Select value={role} onValueChange={setRole}>
                  <SelectTrigger>
                    <SelectValue placeholder="Selecciona un rol" />
                  </SelectTrigger>
                  <SelectContent>
                    {Object.entries(PARTY_ROLE_LABELS).map(([value, label]) => (
                      <SelectItem key={value} value={value}>
                        {label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            {selectedType === 'person' && (
              <div className="space-y-2">
                <Label>ID de Persona (UUID)</Label>
                <Input
                  placeholder="xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx"
                  value={personId}
                  onChange={(e) => setPersonId(e.target.value)}
                  disabled={isSubmitting}
                />
                <p className="text-xs text-muted-foreground">
                  Ingresa el UUID de una persona existente del catálogo
                </p>
              </div>
            )}

            {selectedType === 'company' && (
              <div className="space-y-2">
                <Label>ID de Empresa (UUID)</Label>
                <Input
                  placeholder="xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx"
                  value={companyId}
                  onChange={(e) => setCompanyId(e.target.value)}
                  disabled={isSubmitting}
                />
                <p className="text-xs text-muted-foreground">
                  Ingresa el UUID de una empresa existente del catálogo
                </p>
              </div>
            )}

            <Button onClick={handleAddParty} disabled={isSubmitting}>
              <Plus className="mr-2 h-4 w-4" />
              Agregar Parte
            </Button>
          </CardContent>
        </Card>
      )}

      <Separator />

      {/* Parties List */}
      <div className="space-y-4">
        <h3 className="text-lg font-semibold">Partes Asociadas</h3>

        {isLoading && <p className="text-muted-foreground">Cargando partes...</p>}

        {!isLoading && parties.length === 0 && (
          <Alert>
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>
              No hay partes asociadas. {isViewMode ? '' : 'Agrega la primera parte arriba.'}
            </AlertDescription>
          </Alert>
        )}

        {parties.map((party) => (
          <Card key={party.id}>
            <CardHeader className="pb-3">
              <div className="flex items-start justify-between">
                <div className="space-y-2">
                  <div className="flex items-center gap-2">
                    {party.person ? (
                      <User className="h-4 w-4 text-blue-500" />
                    ) : (
                      <Building className="h-4 w-4 text-purple-500" />
                    )}
                    <CardTitle className="text-base">
                      {party.person?.fullName || party.company?.legalName || 'Sin nombre'}
                    </CardTitle>
                  </div>
                  <div className="flex items-center gap-2">
                    <Badge variant="outline">
                      {PARTY_ROLE_LABELS[party.role] || party.role}
                    </Badge>
                  </div>
                </div>
                {!isViewMode && (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => handleRemoveParty(party.id)}
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                )}
              </div>
            </CardHeader>
            {(party.person?.email || party.company?.taxId) && (
              <CardContent className="pt-0 text-sm text-muted-foreground">
                {party.person?.email && <p>Email: {party.person.email}</p>}
                {party.person?.phone && <p>Teléfono: {party.person.phone}</p>}
                {party.company?.tradeName && <p>Nombre comercial: {party.company.tradeName}</p>}
                {party.company?.taxId && <p>RUT: {party.company.taxId}</p>}
              </CardContent>
            )}
          </Card>
        ))}
      </div>
    </div>
  );
}
